import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchJoborderlpos,
    createJobOrderLpo,
    updateJobOrderLpo,
    deleteJobOrderLpo,
    reverseJobOrderLpo,
    setPagination,
    setSort,
    searchJoborderlpos,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/joborderlpo/joborderlpoActions';
import { jobStatus } from '../../utils/statusUtil';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Joborderlpos = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [] }) => {

    const hasCreatePermission = useHasPermission(['create job order lpos']);
    const hasUpdatePermission = useHasPermission(['update job order lpos']);
    const hasViewPermission = useHasPermission(['view job order lpos']);
    const hasReversePermission = useHasPermission(['reverse job orders']);

    const joborderlpoState = useSelector(state => state.joborderlpo);
    const dispatch = useDispatch();

    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createJobOrderLpo(formData));
            toast.success('Job Order LPO created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating joborderlpo', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateJobOrderLpo(id, formData));
            toast.success('Job Order LPO updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating joborderlpo', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteJobOrderLpo(id));
                toast.success('JobOrderLpo deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting joborderlpo', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const handleReversal = async (row) => {
        const orderNumber = row.order_number;

        const result = await MySwal.fire({
            title: 'Reverse Job Order LPO?',
            html: `<p>This will reverse all issued stock for <strong>${orderNumber}</strong> back into inventory.</p>
                   <p>Labour records will be zeroed and the job order status will be rolled back to <strong>Open</strong>.</p>
                   <p class="text-warning mb-0"><i class="fas fa-exclamation-triangle"></i> This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ffc107',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, reverse it!',
            cancelButtonText: 'Cancel',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(reverseJobOrderLpo(row.id));
                toast.success(`Job Order LPO ${orderNumber} reversed successfully`, {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                dispatch(fetchJoborderlpos());
            } catch (error) {
                toast.error(error.message || 'Error reversing job order lpo', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
            }
        }
    };

    const columns = [
        {
            name: 'Job Order Number',
            selector: row => row.order_number,
            wrap: true,
            minWidth: '180px'
        },
        {
            name: 'LPO Number',
            selector: row => row.lpo_number,
            wrap: true,
            minWidth: '150px'
        },
        {
            name: 'Branch Name',
            selector: row => row.branch_name || 'N/A',
            wrap: true,
            minWidth: '150px'
        },
        {
            name: 'Employee',
            selector: row => row.employee_name,
            wrap: true,
            minWidth: '150px'
        },
        {
            name: 'Job Date',
            selector: row => row.order_date,
            sortField: 'job_orders.order_date',
            sortable: true,
            wrap: true,
            minWidth: '120px'
        },
        {
            name: 'Name',
            selector: row => row?.customer_name,
            wrap: true,
            minWidth: '200px'
        },
        {
            name: 'Net Amount',
            selector: row => row.net_total,
            wrap: true,
            minWidth: '100px'
        },
        {
            name: 'Status',
            selector: row => jobStatus(row.status_id, row.status),
            sortField: 'job_orders.status',
            sortable: true,
            wrap: true,
            minWidth: '120px'
        },
        {
            name: 'Created At',
            selector: row => row.created_at,
            sortField: 'job_orders.created_at',
            sortable: true,
            wrap: true,
            minWidth: '150px'
        }
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchJoborderlpos(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchJoborderlpos(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];

    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Job Order By LPO"
                moduleName="JobOrderLpo"
                moduleKey="joborderlpos"
                state={joborderlpoState}
                actions={actions}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                actionsColumnPosition={5}
                onSelectedRowsChange={onSelectedRowsChange}
                urlKey="job_order_lpos"
                actionType="url"

                selectedIds={selectedIds}
                externalCondition={externalCondition}
                customActions={[
                    {
                        key: 'reverse',
                        label: 'Reverse',
                        icon: 'fas fa-undo',
                        buttonClass: 'btn-warning',
                        condition: (row) => row.can_reverse && hasReversePermission,
                    },
                ]}
                handleCustomAction={(actionKey, row) => {
                    if (actionKey === 'reverse') {
                        handleReversal(row);
                    }
                }}
            />
        </>
    );
};

export default Joborderlpos; 